import { Injectable } from '@angular/core';
import {
    HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

import { AuthenticationService } from '../services/auth.service';
import { isAuthRoute, isPublicRoute } from '../services/public-routes';

/** Llamadas de autenticación cuyo 401 no es "sesión vencida" y nunca deben disparar refresh. */
const AUTH_URL_FRAGMENTS = ['/auth/refresh', '/auth/login', '/usuario-sistema/change-password'];

const FORBIDDEN_DEFAULT_MESSAGE = 'No tienes permiso para realizar esta acción.';
const FORBIDDEN_DEBOUNCE_MS = 4000;

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    /** Resultado del refresh en curso: emite el token nuevo o propaga el error a las peticiones en cola. */
    private refreshSubject = new ReplaySubject<string>(1);
    private lastForbiddenNoticeAt = 0;

    constructor(
        private authenticationService: AuthenticationService,
        private router: Router
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const currentUser = this.authenticationService.getLoggedUser();
        if (currentUser?.token) {
            request = this.attachToken(request, currentUser.token);
        }

        return next.handle(request).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse) {
                    if (error.status === 401 && !this.isAuthUrl(request.url)) {
                        // Sin sesión (p. ej. visitante de un enlace público): no hay nada que renovar ni cerrar.
                        if (!this.authenticationService.getLoggedUser()?.token) {
                            return throwError(() => error);
                        }
                        return this.handle401(request, next);
                    }
                    if (error.status === 403 && !isAuthRoute(this.router.url)) {
                        this.notifyForbidden(error);
                    }
                }
                return throwError(() => error);
            })
        );
    }

    private isAuthUrl(url: string): boolean {
        return AUTH_URL_FRAGMENTS.some(fragment => url.includes(fragment));
    }

    private attachToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            // Queue concurrent requests until refresh completes
            return this.refreshSubject.pipe(
                take(1),
                switchMap(token => next.handle(this.attachToken(request, token)))
            );
        }

        this.isRefreshing = true;
        const refresh$ = new ReplaySubject<string>(1);
        this.refreshSubject = refresh$;

        return this.authenticationService.refreshToken().pipe(
            catchError(err => {
                // Falló el refresh: se rechazan también las peticiones en cola y se cierra la sesión (no en rutas públicas).
                this.isRefreshing = false;
                refresh$.error(err);
                if (!isPublicRoute(this.router.url)) {
                    this.authenticationService.logout();
                    this.router.navigate(['/auth/login']);
                }
                return throwError(() => err);
            }),
            switchMap(newToken => {
                this.isRefreshing = false;
                refresh$.next(newToken);
                refresh$.complete();
                // Si el reintento vuelve a dar 401 (o 403), el error se relanza tal cual.
                return next.handle(this.attachToken(request, newToken)).pipe(
                    catchError(retryError => {
                        if (retryError instanceof HttpErrorResponse && retryError.status === 403
                            && !isAuthRoute(this.router.url)) {
                            this.notifyForbidden(retryError);
                        }
                        return throwError(() => retryError);
                    })
                );
            })
        );
    }

    /** Aviso breve no bloqueante para 403; no cierra sesión ni redirige. */
    private notifyForbidden(error: HttpErrorResponse): void {
        const now = Date.now();
        if (now - this.lastForbiddenNoticeAt < FORBIDDEN_DEBOUNCE_MS) {
            return;
        }
        this.lastForbiddenNoticeAt = now;

        const serverMessage = error.error?.messages?.[0];
        const message = typeof serverMessage === 'string' && serverMessage.trim()
            ? serverMessage
            : FORBIDDEN_DEFAULT_MESSAGE;

        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'warning',
            titleText: message,
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true
        });
    }
}
